import React, { FC, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { KostenPunkt } from '@codefluegel/wowis-rest-typescript-client';

import Selector from '../selector/Selector';

interface Props {
  allYears: string[];
  data?: KostenPunkt[];
  backgroundColors: string[];
}
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const BarChart: FC<Props> = observer(props => {
  const { allYears, data, backgroundColors } = props;

  // state of selected year
  const [selectedBarChartYear, setSelectedBarChartYear] = useState('');

  // state of selected category
  const [selectedCategory, setSelectedCategory] = useState('');

  // filter data by selected year state
  const [filteredData, setFilteredData] = useState<KostenPunkt[] | undefined>(
    data,
  );

  const lastThreeYears = allYears
    .slice(
      allYears.indexOf(selectedBarChartYear),
      allYears.indexOf(selectedBarChartYear) + 3,
    )
    .sort();

  // get last three years from all years
  const getLastThreeYearsData = (costsData: KostenPunkt[]): KostenPunkt[] => {
    const filteredYears = costsData.filter((cost: KostenPunkt) => {
      if (lastThreeYears?.includes(cost.jahr.toString())) {
        return cost;
      }
      return null;
    });

    const updatedData = [...filteredYears];

    const descriptions = Array.from(
      new Set(filteredYears.map(entry => entry.bez)),
    );

    descriptions.forEach(description => {
      lastThreeYears.forEach(year => {
        if (
          !filteredYears.some(
            entry => entry.bez === description && entry.jahr === Number(year),
          )
        ) {
          updatedData.push({
            kat: 'Betriebskosten',
            bez: description,
            jahr: Number(year),
            betrag: 0,
          });
        }
      });
    });

    return updatedData;
  };

  // get all categories from costs data array and remove duplicates
  const getCatergories = (costsData: KostenPunkt[]): string[] => {
    const catergories: string[] = [];
    costsData.forEach(cost => {
      if (!cost.bez) return;
      if (!catergories.includes(cost.bez)) {
        catergories.push(cost.bez);
      }
    });
    return catergories;
  };

  const categories = getCatergories(filteredData || []);

  useEffect(() => {
    if (!data) return;

    const filterResult = getLastThreeYearsData(data);

    setFilteredData(filterResult);
    if (selectedBarChartYear === '') setSelectedBarChartYear(allYears[0]);

    // setting initial category plus preventing category switch during year change
    if (selectedCategory === '' && categories.length > 0) {
      setSelectedCategory(categories[0]);
    }
  }, [selectedBarChartYear, selectedCategory, data]);

  const options = {
    indexAxis: 'y' as const,
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    plugins: {
      legend: {
        position: 'right' as const,
      },
      title: {
        display: true,
        text: 'Ausgaben der letzten 3 Jahre',
      },
    },
  };

  // filter data by selected category and selected year
  const filteredDataByCategory = filteredData
    ?.filter((cost: KostenPunkt) => cost.bez === selectedCategory)
    .sort((a, b) => a.jahr - b.jahr);

  // get betrag from filtered data
  const betrag = filteredDataByCategory?.map((cost: KostenPunkt) =>
    Math.abs(cost.betrag),
  );

  const chartData = {
    labels: lastThreeYears,
    datasets: [
      {
        label: selectedCategory,
        data: betrag,
        borderColor: backgroundColors,
        backgroundColor: backgroundColors,
      },
    ],
  };

  return (
    <Box
      minHeight={500}
      width={1}
      display="flex"
      flexDirection="column"
      justifyItems="center"
      alignItems="center"
    >
      <Box width={1} display="flex" justifyContent="center">
        <Selector
          label="Kategorie"
          data={categories}
          value={selectedCategory}
          setSelectedValue={setSelectedCategory}
        />
        <Selector
          label="Jahr"
          data={allYears || ['']}
          value={selectedBarChartYear || ''}
          setSelectedValue={setSelectedBarChartYear}
        />
      </Box>
      <Bar options={options} data={chartData} />
    </Box>
  );
});

export default BarChart;
